/**
 * Project Bobcat feature flags.
 *
 * Every Bobcat surface is gated on a `bobcat-*` LaunchDarkly flag that is OFF
 * by default, so the `project-bobcat` branch can be rebased, merged or
 * discarded without exposing a demo surface to anyone. The demo itself is a
 * flag flip.
 *
 * ## Why these keys are declared here and not read off `AvailableFeatureFlagKeys`
 *
 * `AvailableFeatureFlagKeys` (`@pypestream/feature-flags`) is AUTO-GENERATED
 * from whatever LaunchDarkly currently serves — `packages/feature-flags/builded/index.d.ts`
 * opens with "do not manually modify this auto-generated file!!". So a flag
 * cannot be added to that union by hand: it has to exist in LaunchDarkly first
 * and then be picked up by a codegen run.
 *
 * That ordering would otherwise block every Bobcat win on an LD change, so the
 * keys live here as their own union and `resolveBobcatAccess` takes a plain
 * `Record<string, boolean>` — exactly what `resolvePypeNextAccess` already
 * does. A key that LaunchDarkly has never heard of is simply absent from the
 * map, which resolves to `denied`. That is the desired default, so an
 * un-created flag fails closed rather than breaking the build.
 *
 * ## Do NOT constrain these keys with `satisfies AvailableFeatureFlagKeys`
 *
 * It is the obvious-looking hardening and it would break the build on any
 * machine whose flag types are stale. `packages/feature-flags/builded/` is
 * **gitignored** (`packages/feature-flags/.gitignore`: `builded/*`), so
 * `AvailableFeatureFlagKeys` is a LOCAL artifact regenerated per machine by
 * `yarn build` in that package — which needs `LAUNCH_DARKLY_NODE_SDK`. A
 * checkout that has never run that codegen, or ran it before these flags
 * existed, would fail to compile on the `satisfies` clause rather than
 * degrading gracefully. Keeping the union local makes this file machine- and
 * codegen-independent, which is the property that matters for a demo branch.
 *
 * The cost is that nothing checks these strings: a typo
 * (`'bobcat-agent-veiw'`) compiles, lints and passes every test in
 * `bobcat-flags.test.ts` — the tests derive their expectations from
 * `BOBCAT_FLAGS` itself, so they cannot catch it. The failure is silent and
 * one-directional: the flag reads as ON in LaunchDarkly while the surface
 * stays hidden, with no error anywhere. **Cross-check any new key against the
 * LaunchDarkly dashboard by eye.**
 *
 * ## The flag must be available to client-side SDKs
 *
 * The browser SDK only receives flags marked *available to client-side SDKs* in
 * LaunchDarkly — which is why the type codegen passes `clientSideOnly: true`
 * (`packages/feature-flags/src/index.ts`). A flag created WITHOUT that toggle
 * is simply absent from `ldClient.allFlags()`, so it resolves to `denied` even
 * when switched ON. That is the most likely reason a demo flip appears to do
 * nothing — check the toggle before debugging this file.
 *
 * A flag that has NOT been created in LaunchDarkly is absent from the map and
 * resolves to `denied`, which is exactly the intended default.
 *
 * `bobcat-grid-grouping` used to be listed here. It was deleted on 2026-09-05
 * along with Win 1.1: node grouping is already complete in Pro Studio, so the
 * win had nothing to build and the flag never gained a consumer. A registry
 * entry for a flag that no longer exists in LaunchDarkly is worse than no
 * entry — `__bobcat__.status()` would report a phantom flag as permanently
 * off.
 *
 * `bobcat-agent-view` used to be listed here (Proposal 1's read-only Agent
 * View tab, Win 1.2). It was deleted on 2026-09-23: the Build-Stage canvas
 * reached feature parity with it (real-group rendering, the "Ungrouped: N"
 * indicator) and Elana confirmed the transitional page could go — "the
 * agent view can go away - the whole design is going to match mostafa's
 * design and the 5 step process." Same reasoning as `bobcat-grid-grouping`'s
 * removal: a registry entry for a flag with no surface left to gate is worse
 * than no entry.
 */

/**
 * One key per Bobcat surface. Add a key here when a win needs its own gate;
 * do not reuse another win's flag, or the two cannot be demoed separately.
 */
export const BOBCAT_FLAGS = {
  /**
   * Automatic Supervisor / Knowledge Agent node grouping on solution load.
   * Its own flag because it MUTATES the solution (it writes group rows), so
   * it must be switchable independently of any read-only view of the same
   * data, and it is the flag a future UI setting would bind to.
   */
  autoAgentGrouping: 'bobcat-auto-agent-grouping',
  /**
   * Editing on the Build canvas: Add agent, a card's add-node and delete
   * actions, a node's delete, dropping a Resource Library item on empty
   * canvas to start a new agent, and drawing Supervisor -> agent. Its own
   * flag because it turns a read-only view into one that WRITES the
   * solution, which must be switchable apart from the canvas itself.
   */
  canvasEditing: 'bobcat-canvas-editing',
  /**
   * Proposal 1's Solution -> Agent vocabulary. Its own flag because it is pure
   * COPY: it can be shown to a prospect without any of the other surfaces, and
   * it is the one most likely to need turning off for a customer-facing build
   * while product signs the wording off.
   */
  agentVocabulary: 'bobcat-agent-vocabulary',
  /**
   * Cell property editor — opens the Expanded Cell View panel on a plain cell
   * CLICK, where today it opens only on Shift+Enter (PSTUDIO-3624).
   *
   * Gated because it changes a gesture on shipped UI. Every click in the grid
   * currently just selects; with this on, a click also opens a side panel.
   * That is the intended design but it is not a change to make for everyone
   * without a switch, and it is the kind of thing that reads as a bug to
   * someone who did not ask for it.
   *
   * Its own flag rather than riding another surface's: this is the Workflows
   * table, which is shipped surface, whereas the other gated surfaces are
   * new additions. They have to be switchable apart.
   */
  cellPropertyEditor: 'bobcat-cell-property-editor',
  /**
   * The Test View tab, a segment beside Table View (and, while it existed,
   * Agent View).
   *
   * Its own flag rather than riding another tab's: they are separate
   * surfaces on separate schedules, and the documented rule for this registry
   * is that a win never reuses another win's flag — otherwise the two cannot
   * be demoed apart. The segment itself is hidden when this is not `allowed`,
   * so nobody can navigate to a view that is switched off.
   */
  testView: 'bobcat-test-view',
  /**
   * The "Create Solution (conversational)" entry point on the Home page and
   * its modal — a scripted chat alternative to the form wizard that reuses
   * `useSolutionWizardForm`'s `createSolution` call. Its own flag because it
   * is a brand-new entry point on a page every user sees, not a change
   * inside an already-gated surface.
   */
  conversationalCreate: 'bobcat-conversational-create',
  /**
   * The Knowledge Agent step's Skip button and restored paste-content
   * textarea (`step-agent.tsx`, in the SHIPPED Create Solution wizard).
   *
   * Gated for the same reason as `cellPropertyEditor`: it changes behavior
   * on shipped UI that every Create Solution user reaches, not a new
   * Bobcat-only surface. Off by default, this step behaves exactly as it did
   * before — file upload only, brand name required to proceed.
   */
  knowledgeAgentIntake: 'bobcat-knowledge-agent-intake',
  /**
   * The Parameter Input field's section drill-down, inside the
   * `cellPropertyEditor` panel's "Action node" tab.
   *
   * Its own flag rather than riding `cellPropertyEditor`: this changes how
   * ONE field renders inside a panel that is already live for anyone with
   * that flag on, so the two need to be switchable apart — someone can have
   * the panel without yet having this refinement to it.
   */
  parameterInputDrilldown: 'bobcat-parameter-input-drilldown',
  /**
   * Auto-publishes a brand-new solution's first version the moment it
   * becomes publish-ready, so it is testable immediately without an
   * explicit Publish click. Its own flag because it TAKES A REAL ACTION
   * (publishes to the sandbox environment) rather than changing a view —
   * the kind of thing that must be switchable independently and off by
   * default even more than most Bobcat surfaces.
   */
  autoPublishOnCreate: 'bobcat-auto-publish-on-create',
  /**
   * The Knowledge Agent global-settings panel's SOP front door: a
   * paste/describe box that calls Claude to derive Brand Name, Tone and the
   * Knowledge Agent's global instructions, shown for direct edit afterward.
   * Its own flag because it is Pro Studio's FIRST-EVER call to an LLM from
   * code this app owns (verified: no prior precedent anywhere in the repo)
   * — the new server route it depends on should be switchable independently
   * of every other Bobcat surface while that gets proven out.
   *
   * Also gates exposing `knowledgeAgentContext` as a directly-editable field
   * on this panel at all — today it exists on the schema and round-trips,
   * but is only ever written from the Create Solution wizard's gated step
   * (`bobcat-knowledge-agent-intake`). Independent of that flag: a solution
   * can have this panel's exposure on without the wizard's intake step, or
   * vice versa.
   */
  knowledgeAgentGlobalInstructions:
    'bobcat-knowledge-agent-global-instructions',
  /**
   * "Manage Intents": a structured editor for a `SysMultiMatchRouting`
   * node's routing table, replacing the Parameter Input drill-down's generic
   * section list for that one command with a name/destination list that
   * writes BOTH `input_vars` (Parameter Input) and `What Next?` together.
   *
   * Its own flag rather than riding `parameterInputDrilldown`: this changes
   * how one command's Parameter Input renders inside a panel that's already
   * live for anyone with that flag on, and — unlike the generic drill-down —
   * it writes a SECOND column (`What Next?`) as part of the same edit, which
   * is a bigger behavior change than reformatting a read/write on one field.
   * Someone can have the generic drill-down without yet having this
   * command-specific refinement to it.
   */
  manageIntentsRouting: 'bobcat-manage-intents-routing',
  /**
   * Build Stage canvas — Phase 1 (static rendering only): each of a
   * solution's real agent groups as a colored, auto-laid-out node on a
   * pannable/zoomable React Flow surface. Its own flag rather than riding
   * another surface's: this is new render tech (React Flow, an auto-layout
   * library) on an additive surface, not a refinement of the existing grid
   * projection, and needs to be switchable independently while that tech is
   * proven out.
   *
   * Phase 1 only: static colored nodes + pan/zoom. No edges, no group
   * filter, no search, no node-details panel, no agents dock, no
   * Canvas/Table switcher — those are later phases and may end up gated
   * separately; see the Protolab→Bobcat scoping doc.
   */
  buildCanvas: 'bobcat-build-canvas',
  /**
   * Drag a Resource Library card (Microagent or Action Node) onto a Table
   * View row to add it there, instead of the always-appends-at-the-end
   * click-to-add flow. Its own flag rather than riding any existing one:
   * this changes a gesture on the Resource Library panel, which is shipped
   * surface every Table View user already has open access to — it must stay
   * switchable independently of every other Bobcat win, same reasoning as
   * `cellPropertyEditor`.
   */
  resourceLibraryDragDrop: 'bobcat-resource-library-drag-drop',
  /**
   * Propose real single-member groups for the "Create Solution
   * (conversational, beta)" onboarding flow's use-case stub nodes — the
   * ones `task-utils.ts` stamps with `settings.taskId` and leaves ungrouped
   * (`use-case-agent-numbering.ts`). Its own flag rather than riding
   * `autoAgentGrouping`: like that feature it MUTATES the solution (writes
   * group rows) when applied, so it must be switchable independently and
   * off by default even more than a read-only surface — and it detects a
   * completely different shape (the task-node marker, not the Supervisor/
   * Knowledge name convention), so a customer build could have one on
   * without the other.
   */
  useCaseAgentNumbering: 'bobcat-use-case-agent-numbering',
  /**
   * Create builds real agents, not one-node stubs: a task that matches an
   * agent template (`server-only/ai/agent-templates.ts`) is built from it,
   * the rest are drafted by AI (`POST /ai/agents/draft`). Each agent records
   * where it came from (`settings.agentSource` on its group row), which the
   * canvas and Design show, and the Resource Library lists the templates.
   * Writes rows after a solution is created, so its own flag.
   */
  agentTemplates: 'bobcat-agent-templates',
  /**
   * After Create, a guided setup takes the new solution through the five
   * stages (`pages/guided-setup/`): the stage tabs numbered and locked until
   * reached, a coach card with the next step, and Publish held back until
   * Deploy. Per browser, per solution; Exit ends it.
   */
  guidedSetup: 'bobcat-guided-setup',
  /**
   * Canvas Phase 3, build A — click a node inside an agent card to open the
   * same `NodePropertyPanel` Table View's cell click opens, docked on
   * canvas's right side. Its own flag rather than riding `buildCanvas` or
   * `cellPropertyEditor`: it is a new INTERACTION on the canvas surface
   * specifically (the click target, the docking), independent of whether
   * canvas itself is on (`buildCanvas`) or whether Table View's own panel is
   * on for a given build (`cellPropertyEditor`) — a build can have canvas
   * without this, or this without Table View's cell-click affordance.
   */
  canvasNodeDetailPanel: 'bobcat-canvas-node-detail-panel',
  /**
   * Canvas Phase 3, build B — the agent list sidebar (filter, All/None/Only
   * routed, Collapse/Expand all, per-agent visibility), node-type dots on
   * each card's member rows, and the static node-type/edge legend. One flag
   * for all three rather than splitting them: they are tightly coupled (a
   * sidebar with no styled rows to filter is not a real feature, and the
   * legend explains dots this same build introduces), and — unlike
   * `canvasNodeDetailPanel`, which changes what a CLICK does — none of the
   * three take any action on the solution, so there is no independent
   * "on/off" combination among them worth a customer being able to choose.
   * Independent of `canvasNodeDetailPanel`: a build can have the sidebar
   * without the node-click panel, or vice versa.
   */
  canvasAgentSidebar: 'bobcat-canvas-agent-sidebar',
  /**
   * Canvas Phase 3, build C — click a card to dim every agent (and every
   * edge/label) it is not connected to, and to give edge labels the pill
   * background the live Protolab prototype uses. Ground truth pulled
   * directly from that prototype's own source this session
   * (`prostudio-build-flow-layouts.html`'s `cvLit`/`is-dim` mechanism, real
   * opacities: cards 0.62, wires/dots 0.18, labels 0.3), not inferred from a
   * screenshot.
   *
   * Its own flag rather than riding `canvasAgentSidebar` or
   * `canvasNodeDetailPanel`: the click-to-select mechanism itself (the
   * highlighted border, error-routing edges, flow edges gated to the
   * selected agent) already ships unconditionally from Phase 2 — this flag
   * gates only the NEW dim/pill layer on top, so a build can keep the
   * existing selection behavior without this polish, or turn this off
   * independently while the other two builds' surfaces stay on.
   */
  canvasRoutingHighlight: 'bobcat-canvas-routing-highlight',
  /**
   * The Design/Build/Validate/Deploy/Observe stage nav that replaces the
   * single "Agent Workflow" tab in the header, on the real Protolab
   * prototype's own model (`prostudio-build-flow-layouts.html`, checked live
   * this session — see "PR Spec: 5-Stage Nav"). Phase 1 only: the tab
   * control itself, real Table View/Canvas re-nested under "Build", and
   * honest placeholders for the other four stages — not their real content,
   * which is separately-phased, separately-flagged work.
   *
   * Its own flag rather than riding `buildCanvas` or any other Bobcat surface
   * flag: this changes the primary navigation shell of the whole Workflows
   * page — the single biggest UI change in Project Bobcat so far — and must
   * be switchable independently of every flag underneath it. With this OFF,
   * the header and page render EXACTLY as they did before this flag existed:
   * additive, not destructive.
   *
   * `routes.actions`'s own nav tab ("Actions") was left unchanged by this
   * flag until the 2026-09-29 status check placed it: with the flag on, it
   * is an icon on the Build bar that opens Build's Actions view, the tab is
   * hidden, and the old route redirects there — as Files did before it.
   */
  fiveStageNav: 'bobcat-five-stage-nav',
  /**
   * AI Action Assist: "Build with AI" in the Ask AI dock when an action node
   * is selected, opening a builder that drafts the node's fields from a brief
   * (Assisted build). Calls Claude through Pro Studio's own
   * `/ai/action-assist/draft` route. Questions about the solution are the
   * separate `solutionAssistant` surface.
   *
   * Its own flag for the same reason as `knowledgeAgentGlobalInstructions`:
   * it is a new LLM surface on its own server routes, so it has to be
   * switchable independently of every other Bobcat surface while it is
   * proven out, and independently of the node panel it lives in. Off means
   * no button at all.
   */
  aiActionAssist: 'bobcat-ai-action-assist',
  /**
   * The solution assistant: an "Ask AI" button in the header, on a
   * solution's Workflows page, opening a right-side dock that answers
   * questions about the whole solution (or the selected node) through
   * `/ai/action-assist/chat`. It moved out of AI Action Assist because it is
   * a multi-purpose tool, not part of writing one action node.
   *
   * Its own flag rather than riding `aiActionAssist`: it is a new surface in
   * the shared header on every solution, reachable from every stage, so it
   * has to be switchable apart from the node-panel builder — either can be
   * on without the other. Needs `fiveStageNav` too: the dock sits beside the
   * 5-stage content. Off means no header button and no dock.
   */
  solutionAssistant: 'bobcat-solution-assistant',
  /**
   * The Validate stage built from the testing tool's pieces: a live Test
   * chat over the Engagement API socket (`@pypestream/testview`), simulated
   * scenarios, every reply scored on the Generative AI Evaluation Rubric
   * through `/ai/validate/*`, and a run history with trace links.
   *
   * Its own flag: it is a new LLM surface on its own server routes, and it
   * opens real conversations on a solution's channels. Needs `fiveStageNav`
   * (it is the Validate stage's content). Off means Validate keeps today's
   * Test View.
   */
  validateStage: 'bobcat-validate-stage',
} as const;

export type BobcatFlagName = keyof typeof BOBCAT_FLAGS;
export type BobcatFlagKey = (typeof BOBCAT_FLAGS)[BobcatFlagName];

/**
 * Tri-state, for the same reason `resolvePypeNextAccess` is tri-state:
 * `ctx.featureFlags` is `undefined` until LaunchDarkly answers, which is after
 * first render, and `WithLoadingState` renders a page during loading rather
 * than holding it back. Collapsing `undefined` into "off" (the `?? false` most
 * flag consumers use) makes a reload or a deep link redirect straight back out
 * of the surface — which on stage reads as the demo being broken.
 *
 * `pending` is bounded: a LaunchDarkly failure reports an empty flag map rather
 * than staying silent (PSTUDIO-3768), so this resolves to `denied` instead of
 * hanging forever.
 */
export type BobcatAccess = 'pending' | 'allowed' | 'denied';

export const resolveBobcatAccess = (
  flag: BobcatFlagName,
  featureFlags?: Record<string, boolean>
): BobcatAccess => {
  if (!featureFlags) {
    return 'pending';
  }

  // `=== true` and not a truthiness check, because the boolean in
  // `FeatureFlagsType` is an assumption the runtime does not honour: the values
  // come from `ldClient.allFlags()`, and this repo already has a STRING-valued
  // flag the generated type calls a boolean — `ps-agent-compiler-errors` serves
  // 'off' | 'on' | 'bot_only' (see `agent-compiler-mode.ts`, which exists to
  // parse exactly that). A Bobcat flag accidentally created as a multivariate
  // flag serving the string "off" is therefore truthy, and a truthiness check
  // would open the surface — failing OPEN, the opposite of every other
  // guarantee documented above.
  return featureFlags[BOBCAT_FLAGS[flag]] === true ? 'allowed' : 'denied';
};
